import React from "react";
import styled from "styled-components";

import { noFocusOnMouseDown } from "../../utils";

// Standard themed button. Knobs:
//   variant: 'internal' (boring surface, used inside dense panels) |
//            'default'  (warm chrome, has presence — landing CTAs, modal
//                       primary actions) |
//            'primary'  (warn-orange CTA — the highest-emphasis action)
//   size:    'small' | 'medium' | 'large'
//   $active: selected state, swaps bg/fg to selected-* tokens
//
// Visual values are exposed as CSS custom properties (--background,
// --text-color, --border-color, --padding, --font-size). Variant + size
// just set those; the style rules consume them. To tweak a single instance,
// set the var via inline style (`style={{ '--background': 'red' }}`) or
// extend with `styled(Button)\`--padding: 14px 28px;\``.
//
// `noFocusOnMouseDown` is built in so callers don't need to remember it.
function Button({
  variant = "default",
  size = "medium",
  $active = false,
  children,
  ...rest
}) {
  return (
    <Root $variant={variant} $size={size} $active={$active} {...rest}>
      {children}
    </Root>
  );
}

const SIZES = {
  small: `
    --padding: 6px 12px;
    --font-size: 13px;
  `,
  medium: `
    --padding: 8px 16px;
    --font-size: 1rem;
  `,
  large: `
    --padding: 0.5rem 1rem;
    --font-size: 1.125rem;
  `,
};

const VARIANTS = {
  internal: `
    --background: var(--color-surface);
    --text-color: var(--color-text);
    --border-color: var(--color-border);
    --hover-background: var(--color-surface-hover);
  `,
  default: `
    --background: var(--color-chrome);
    --text-color: var(--color-text);
    --border-color: var(--color-stone-500);
    --hover-background: var(--color-orange-300);
  `,
  primary: `
    --background: var(--color-warn);
    --text-color: var(--color-selected-fg);
    --border-color: var(--color-warn);
    --hover-background: var(--color-warn-hover);
  `,
};

const ACTIVE = `
  --background: var(--color-selected-bg);
  --text-color: var(--color-selected-fg);
`;

const Root = styled.button.attrs({
  onMouseDown: noFocusOnMouseDown,
})`
  ${(p) => SIZES[p.$size] ?? SIZES.medium}
  ${(p) => VARIANTS[p.$variant] ?? VARIANTS.default}
  ${(p) => p.$active && ACTIVE}

  padding: var(--padding);
  font-size: var(--font-size);
  background: var(--background);
  color: var(--text-color);
  border: 1px solid var(--color-border-subtle);
  box-shadow: var(--surface-box-shadow);

  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition:
    background-color 120ms ease-out,
    color 120ms ease-out;

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  :has(:hover) &:hover {
    background-color: var(--hover-background);
  }

  &:focus-visible {
    outline: 2px solid var(--color-text);
    outline-offset: 2px;
  }
`;

export default Button;
